import React, { useState, useRef } from "react";
import { Paperclip, FileText, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "../../../../../lib/utils";
import {
  card,
  cardTitle,
  microLabel,
  surfaceMuted,
  motionFadeUp,
} from "../../../../../lib/design-system";

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

const DiscussionForm = ({ onSubmit, onCancel, loading }) => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    await onSubmit({ title, content }, attachments);
    setTitle("");
    setContent("");
    setAttachments([]);
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setAttachments((prev) => [...prev, ...Array.from(e.target.files)]);
    }
  };

  return (
    <motion.div {...motionFadeUp} className={cn(card, "p-4 sm:p-6 mb-6")}>
      <h5 className={cn(cardTitle, "mb-4")}>Create New Discussion</h5>
      <div className="h-1 w-8 bg-accent1 rounded-sm mb-4" />

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className={cn(microLabel, "block mb-1.5")}>Title</label>
          <input
            type="text"
            className={cn(
              "w-full p-2.5 border border-gray-200 dark:border-gray-700 rounded-sm",
              "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm",
              "focus:outline-none focus:ring-2 focus:ring-accent1/50 focus:border-accent1",
              "transition-colors"
            )}
            placeholder="Enter discussion title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>

        <div>
          <label className={cn(microLabel, "block mb-1.5")}>Content</label>
          <textarea
            className={cn(
              "w-full p-2.5 border border-gray-200 dark:border-gray-700 rounded-sm",
              "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm",
              "focus:outline-none focus:ring-2 focus:ring-accent1/50 focus:border-accent1",
              "transition-colors resize-none"
            )}
            rows="5"
            placeholder="Enter discussion content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            required
          />
        </div>

        <div>
          <label className={cn(microLabel, "block mb-1.5")}>Attachments</label>
          <div
            className={cn(
              "border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-sm",
              "p-4 text-center",
              surfaceMuted
            )}
          >
            <Paperclip className="w-5 h-5 mx-auto mb-2 text-gray-400 dark:text-gray-500" />
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Drag and drop files here or click to browse
            </div>
            <input
              type="file"
              className="hidden"
              multiple
              ref={fileInputRef}
              onChange={handleFileSelect}
            />
            <button
              type="button"
              className="px-3 py-1.5 text-sm rounded-sm border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              Browse Files
            </button>
          </div>

          {attachments.length > 0 && (
            <div className="mt-2 space-y-1">
              {attachments.map((file, index) => (
                <div
                  key={index}
                  className={cn(
                    "flex items-center p-2 rounded-sm border border-gray-200 dark:border-gray-700",
                    surfaceMuted
                  )}
                >
                  <FileText className="w-4 h-4 text-accent1 mr-2 flex-shrink-0" />
                  <div className="flex-grow min-w-0">
                    <div className="text-xs sm:text-sm truncate text-gray-900 dark:text-gray-100">
                      {file.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {formatFileSize(file.size)}
                    </div>
                  </div>
                  <button
                    className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-1 flex-shrink-0"
                    onClick={() =>
                      setAttachments(attachments.filter((_, i) => i !== index))
                    }
                    type="button"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            className="px-4 py-2 text-sm rounded-sm border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !title.trim() || !content.trim()}
            className={cn(
              "px-4 py-2 text-sm rounded-sm",
              "bg-accent1 text-white hover:bg-accent1/90 transition-colors",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          >
            Create Discussion
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default DiscussionForm;
